import React, { useCallback, useEffect, useRef, useState } from 'react';

const AUTOPLAY_MS = 4500;
const SWIPE_THRESHOLD = 40;

const HomeTeamMobileCarousel = ({ members, swipeHint }) => {
  const count = members.length;
  const [index, setIndex] = useState(0);
  const touchStartX = useRef(0);
  const touchDeltaX = useRef(0);
  const pausedRef = useRef(false);

  const goTo = useCallback(
    (nextIndex) => {
      if (count <= 1) return;
      setIndex(((nextIndex % count) + count) % count);
    },
    [count],
  );

  const goPrev = useCallback(() => goTo(index - 1), [goTo, index]);
  const goNext = useCallback(() => goTo(index + 1), [goTo, index]);

  useEffect(() => {
    setIndex((current) => (current >= count ? 0 : current));
  }, [count]);

  useEffect(() => {
    if (count <= 1) return undefined;

    const timer = window.setInterval(() => {
      if (pausedRef.current) return;
      setIndex((current) => (current + 1) % count);
    }, AUTOPLAY_MS);

    return () => window.clearInterval(timer);
  }, [count]);

  const onTouchStart = (event) => {
    touchStartX.current = event.touches[0].clientX;
    touchDeltaX.current = 0;
    pausedRef.current = true;
  };

  const onTouchMove = (event) => {
    touchDeltaX.current = event.touches[0].clientX - touchStartX.current;
  };

  const onTouchEnd = () => {
    if (touchDeltaX.current <= -SWIPE_THRESHOLD) {
      goNext();
    } else if (touchDeltaX.current >= SWIPE_THRESHOLD) {
      goPrev();
    }
    touchDeltaX.current = 0;
    window.setTimeout(() => {
      pausedRef.current = false;
    }, AUTOPLAY_MS);
  };

  if (!count) return null;

  return (
    <div
      className="home-team-mobile-carousel"
      onMouseEnter={() => {
        pausedRef.current = true;
      }}
      onMouseLeave={() => {
        pausedRef.current = false;
      }}
    >
      <div className="home-team-mobile-carousel__stage">
        {count > 1 && (
          <>
            <div className="home-team-mobile-carousel__counter" aria-live="polite">
              {index + 1} / {count}
            </div>
            <div className="home-team-mobile-carousel__edge home-team-mobile-carousel__edge--left" aria-hidden="true" />
            <div className="home-team-mobile-carousel__edge home-team-mobile-carousel__edge--right" aria-hidden="true" />
          </>
        )}

        {count > 1 && (
          <button
            type="button"
            className="home-team-mobile-carousel__arrow home-team-mobile-carousel__arrow--prev"
            onClick={goPrev}
            aria-label="Previous team member"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
              <path d="M15.41 7.41 14 6l-6 6 6 6 1.41-1.41L10.83 12z" fill="currentColor" />
            </svg>
          </button>
        )}

        <div
          className="home-team-mobile-carousel__viewport"
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          <div
            className="home-team-mobile-carousel__track"
            style={{ transform: `translate3d(-${index * 100}%, 0, 0)` }}
          >
            {members.map((member) => (
              <div key={member.id} className="home-team-mobile-carousel__slide">
                <div className="team-inner-wrap">
                  <div className="image-wrap">
                    <img src={member.photo} alt={member.name} loading="lazy" />
                  </div>
                  <div className="team-content">
                    <h3 className="title">{member.name}</h3>
                    <span className="team-title">{member.role}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {count > 1 && (
          <button
            type="button"
            className="home-team-mobile-carousel__arrow home-team-mobile-carousel__arrow--next"
            onClick={goNext}
            aria-label="Next team member"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
              <path d="m10 6-1.41 1.41L13.17 12l-4.58 4.59L10 18l6-6z" fill="currentColor" />
            </svg>
          </button>
        )}
      </div>

      {count > 1 && swipeHint && (
        <p className="home-team-mobile-carousel__hint">{swipeHint}</p>
      )}

      {count > 1 && (
        <>
          <div className="home-team-mobile-carousel__dots" role="tablist" aria-label="Team members">
            {members.map((member, dotIndex) => (
              <button
                key={`dot-${member.id}`}
                type="button"
                role="tab"
                aria-selected={dotIndex === index}
                aria-label={`Show ${member.name}`}
                className={`home-team-mobile-carousel__dot${
                  dotIndex === index ? ' is-active' : ''
                }`}
                onClick={() => goTo(dotIndex)}
              />
            ))}
          </div>

          <div className="home-team-mobile-carousel__thumbs">
            {members.map((member, thumbIndex) => (
              <button
                key={`thumb-${member.id}`}
                type="button"
                className={`home-team-mobile-carousel__thumb${
                  thumbIndex === index ? ' is-active' : ''
                }`}
                onClick={() => goTo(thumbIndex)}
                aria-label={`View ${member.name}`}
                aria-current={thumbIndex === index ? 'true' : undefined}
              >
                <img src={member.photo} alt="" loading="lazy" />
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default HomeTeamMobileCarousel;
